import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';

const LOGO_URL = 'https://static.readdy.ai/image/5b0394d63ea67e242d80d6c69b47f9d5/248fb4068954f3ccbfda9e34faec5a86.png';

const navLinks = [
  { name: 'Home', path: '/' },
  { name: 'About', path: '/about' },
  { name: 'Campaigns', path: '/campaigns' },
  { name: 'Impact', path: '/impact' },
  { name: 'Gallery', path: '/gallery' },
  { name: 'Blog', path: '/blog' },
  { name: 'Contact', path: '/contact' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMobileMenuOpen(false);
      }
    };
    if (mobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [mobileMenuOpen]);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 pt-3 ${
        scrolled
          ? 'bg-white/95 backdrop-blur-xl border-b border-primary-100/60'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8">
        <div className="flex items-center justify-between h-18 md:h-22" style={{ height: '72px' }}>

          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group" aria-label="Happy Faces Hyderabad - Home">
            <div className="relative w-14 h-14 md:w-16 md:h-16 flex-shrink-0">
              {/* Subtle glow ring */}
              <div className={`absolute inset-0 rounded-full transition-all duration-500 ${
                scrolled ? 'ring-2 ring-accent-400/30' : 'ring-2 ring-white/30'
              }`}></div>
              <img
                src={LOGO_URL}
                alt="Happy Faces Hyderabad Rural Development Welfare Society Logo"
                className="w-full h-full rounded-full object-cover object-center drop-shadow-lg transition-transform duration-300 group-hover:scale-105"
              />
            </div>
            <div className="flex flex-col leading-tight">
              <span className={`font-heading text-base md:text-lg font-bold transition-colors duration-300 leading-tight ${
                scrolled ? 'text-primary-800' : 'text-white'
              }`}>
                Happy Faces
              </span>
              <span className={`font-heading text-base md:text-lg font-bold transition-colors duration-300 leading-tight ${
                scrolled ? 'text-primary-800' : 'text-white'
              }`}>
                Hyderabad
              </span>

            </div>
          </Link>

          {/* Desktop nav links */}
          <div className="hidden md:flex items-center gap-0.5 lg:gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`relative px-3 py-2 text-sm font-medium rounded-lg transition-all duration-300 whitespace-nowrap ${
                  location.pathname === link.path
                    ? scrolled
                      ? 'text-primary-700 bg-primary-50 font-semibold'
                      : 'text-white bg-white/20 font-semibold'
                    : scrolled
                      ? 'text-foreground-600 hover:text-primary-700 hover:bg-primary-50/60'
                      : 'text-white/90 hover:text-white hover:bg-white/15'
                }`}
              >
                {link.name}
                {location.pathname === link.path && (
                  <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-accent-500"></span>
                )}
              </Link>
            ))}
          </div>

          {/* Donate CTA */}
          <div className="hidden md:flex items-center gap-3">
            <Link
              to="/donate"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-accent-500 text-primary-900 text-sm font-bold rounded-full hover:bg-accent-400 transition-all duration-300 whitespace-nowrap hover:scale-105"
            >
              <i className="ri-heart-fill text-base text-primary-800"></i>
              Donate Now
            </Link>
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors ${
              scrolled ? 'text-primary-800 hover:bg-primary-50' : 'text-white hover:bg-white/20'
            }`}
            aria-label="Toggle menu"
          >
            <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        ref={menuRef}
        className={`md:hidden transition-all duration-300 overflow-hidden ${
          mobileMenuOpen ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="bg-white/97 backdrop-blur-xl border-t border-primary-100/60 px-4 py-4 space-y-1">
          {/* Mobile logo row */}
          <div className="flex items-center gap-3 px-2 pb-3 border-b border-primary-100/40 mb-3">
            <img
              src={LOGO_URL}
              alt="Happy Faces Hyderabad Logo"
              className="w-10 h-10 rounded-full object-cover"
            />
            <div>
              <p className="text-sm font-bold font-heading text-primary-800">Happy Faces Hyderabad</p>
              <p className="text-[10px] text-accent-600">Rural Development Welfare Society</p>
            </div>
          </div>

          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`block px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 ${
                location.pathname === link.path
                  ? 'text-primary-700 bg-primary-50 font-semibold'
                  : 'text-foreground-700 hover:text-primary-700 hover:bg-primary-50/50'
              }`}
            >
              {link.name}
            </Link>
          ))}
          <div className="pt-3 px-2">
            <Link
              to="/donate"
              className="block w-full text-center px-5 py-3 bg-accent-500 text-primary-900 text-sm font-bold rounded-full hover:bg-accent-400 transition-all duration-300"
            >
              <i className="ri-heart-fill mr-2 text-primary-800"></i>
              Donate Now
            </Link>
          </div>
        </div>
      </div>
    </nav>
  );
}